import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { customerApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Search,
  Plus,
  MapPin,
  Phone,
  Mail,
  Building2,
  User,
  MoreVertical,
  Edit,
  Trash2,
  Activity,
  ExternalLink,
  Crosshair,
  Loader2,
  Navigation,
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  Globe,
  Landmark
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const CATEGORIES = [
  { value: 'municipal_corporation', label: 'Municipal Corporation' },
  { value: 'district_office', label: 'District Office' },
  { value: 'state_dept', label: 'State Department' },
  { value: 'central_dept', label: 'Central Department' },
  { value: 'psu', label: 'PSU' },
  { value: 'other', label: 'Other Government' },
];

const STATES = [
  'Maharashtra', 'Karnataka', 'Tamil Nadu', 'Gujarat', 'Rajasthan', 
  'Uttar Pradesh', 'Madhya Pradesh', 'West Bengal', 'Telangana', 'Andhra Pradesh',
  'Kerala', 'Punjab', 'Haryana', 'Bihar', 'Odisha', 'Delhi'
];

const OFFICE_TYPES = [
  { value: 'head_office', label: 'Head Office' },
  { value: 'regional_office', label: 'Regional Office' },
  { value: 'zonal_office', label: 'Zonal Office' },
  { value: 'branch_office', label: 'Branch Office' },
  { value: 'others', label: 'Others' },
];

export default function CustomersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodingEdit, setGeocodingEdit] = useState(false);
  const [geocodeSuggestions, setGeocodeSuggestions] = useState([]);
  const [geocodeSuggestionsEdit, setGeocodeSuggestionsEdit] = useState([]);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    code: '',
    type: 'government',
    category: 'municipal_corporation',
    address: '',
    city: '',
    state: 'Maharashtra',
    pincode: '',
    location: { lat: 0, lng: 0 },
    website: '',
    office_type: 'head_office',
    office_type_other: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    department: '',
    notes: '',
    tags: [],
  });
  const [fetchingWebsite, setFetchingWebsite] = useState(false);
  const [fetchingWebsiteEdit, setFetchingWebsiteEdit] = useState(false);

  const handleGeocode = async (mode = 'create') => {
    const target = mode === 'create' ? newCustomer : editingCustomer;
    const setLoading = mode === 'create' ? setGeocoding : setGeocodingEdit;
    const setSuggestions = mode === 'create' ? setGeocodeSuggestions : setGeocodeSuggestionsEdit;

    if (!target.address && !target.city) {
      toast.error('Enter an address or city to fetch coordinates');
      return;
    }

    setLoading(true);
    setSuggestions([]);
    try {
      const res = await customerApi.geocode(target.address || '', target.city || '', target.state || '');
      const results = res.data.results || [];
      if (results.length === 0) {
        toast.error('No location found for this address');
      } else if (results.length === 1) {
        // Auto-select single result
        const loc = results[0];
        if (mode === 'create') {
          setNewCustomer(prev => ({ ...prev, location: { lat: loc.lat, lng: loc.lng } }));
        } else {
          setEditingCustomer(prev => ({ ...prev, location: { lat: loc.lat, lng: loc.lng } }));
        }
        toast.success(`Location found: ${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`);
      } else {
        setSuggestions(results);
        toast.info(`${results.length} locations found. Select one.`);
      }
    } catch (err) {
      toast.error('Failed to fetch coordinates');
    } finally {
      setLoading(false);
    }
  };

  const selectGeocodeSuggestion = (suggestion, mode = 'create') => {
    if (mode === 'create') {
      setNewCustomer(prev => ({ ...prev, location: { lat: suggestion.lat, lng: suggestion.lng } }));
      setGeocodeSuggestions([]);
    } else {
      setEditingCustomer(prev => ({ ...prev, location: { lat: suggestion.lat, lng: suggestion.lng } }));
      setGeocodeSuggestionsEdit([]);
    }
    toast.success(`Coordinates set: ${suggestion.lat.toFixed(4)}, ${suggestion.lng.toFixed(4)}`);
  };

  const handleMapClick = (lat, lng, mode = 'create') => {
    if (mode === 'create') {
      setNewCustomer(prev => ({ ...prev, location: { lat, lng } }));
    } else {
      setEditingCustomer(prev => ({ ...prev, location: { lat, lng } }));
    }
    toast.success(`Location pinned: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
  };

  const handleFetchFromWebsite = async (mode = 'create') => {
    const target = mode === 'create' ? newCustomer : editingCustomer;
    const setLoading = mode === 'create' ? setFetchingWebsite : setFetchingWebsiteEdit;
    const url = target.website;

    if (!url) {
      toast.error('Enter a website URL first');
      return;
    }
    setLoading(true);
    try {
      const res = await customerApi.fetchAddressFromWebsite(url);
      const data = res.data;
      if (!data.address && !data.city) {
        toast.error('Could not extract address from website. Try entering manually.');
        return;
      }

      const updates = {};
      if (data.address) updates.address = data.address;
      if (data.city) updates.city = data.city;
      if (data.state) updates.state = data.state;
      if (data.pincode) updates.pincode = data.pincode;
      if (data.location?.lat && data.location?.lng) updates.location = data.location;

      if (mode === 'create') {
        setNewCustomer(prev => ({ ...prev, ...updates }));
      } else {
        setEditingCustomer(prev => ({ ...prev, ...updates }));
      }

      const conf = data.confidence || 'medium';
      toast.success(`Address extracted (${conf} confidence): ${data.full_address || data.address || 'Partial data'}`, { duration: 5000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch address from website');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterState !== 'all') params.state = filterState;
      if (searchQuery) params.search = searchQuery;
      const res = await customerApi.getAll(params);
      setCustomers(res.data);
    } catch (err) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [filterCategory, filterState, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.city || !newCustomer.state) {
      toast.error('Name, city and state are required');
      return;
    }
    if (newCustomer.office_type === 'others' && !newCustomer.office_type_other?.trim()) {
      toast.error('Please specify the office type when "Others" is selected');
      return;
    }

    try {
      await customerApi.create(newCustomer);
      toast.success('Customer created successfully');
      setIsCreateOpen(false);
      setNewCustomer({
        name: '', code: '', type: 'government', category: 'municipal_corporation',
        address: '', city: '', state: 'Maharashtra', pincode: '',
        location: { lat: 0, lng: 0 }, website: '', office_type: 'head_office', office_type_other: '',
        contact_person: '', contact_phone: '',
        contact_email: '', department: '', notes: '', tags: [],
      });
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create customer');
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;

    try {
      await customerApi.update(editingCustomer.id, editingCustomer);
      toast.success('Customer updated successfully');
      setEditingCustomer(null);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update customer');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      await customerApi.delete(customerId);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const openGoogleMaps = (customer) => {
    const { lat, lng } = customer.location || {};
    if (lat && lng) {
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    } else {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(customer.address + ', ' + customer.city)}`, '_blank');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'municipal_corporation': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'district_office': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'state_dept': return 'bg-green-100 text-green-700 border-green-200';
      case 'central_dept': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'owner';
  const isSuperAdmin = user?.role === 'admin' || user?.role === 'owner';

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload a CSV or Excel (.xlsx) file');
      return;
    }
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await customerApi.importFile(formData);
      const d = res.data;
      toast.success(`Import complete: ${d.created} created, ${d.skipped} skipped`);
      if (d.errors?.length) toast.warning(`${d.errors.length} rows had errors`);
      fetchCustomers();
      setIsImportOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterState !== 'all') params.state = filterState;
      const res = await customerApi.exportData(format, params);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format === 'excel' ? 'Excel' : 'PDF'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = 'name,code,category,address,city,state,pincode,contact_person,contact_phone,contact_email,department,latitude,longitude,notes\n';
    const sample = 'Municipal Corporation Mumbai,MCGM,municipal_corporation,"CST Road, Fort",Mumbai,Maharashtra,400001,Mr. Sharma,+91-22-22620151,info@mcgm.gov.in,Water Supply,18.9322,72.8347,Main office\n';
    const blob = new Blob([headers + sample], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_import_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="customers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>Customers</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your customer directory</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Export - admin/owner only */}
            {isSuperAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" disabled={exportLoading} data-testid="export-customers-btn">
                    {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport('excel')} data-testid="export-excel-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                    Export as Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
                    <FileText className="w-4 h-4 mr-2 text-red-600" />
                    Export as PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Import - admin/manager */}
            {isAdmin && (
              <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="import-customers-btn">
                    <Upload className="w-4 h-4" />
                    Import
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Outfit' }}>Import Customers</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-gray-600">Upload a CSV or Excel (.xlsx) file with customer data. Required column: <strong>name</strong>. Optional: code, category, address, city, state, pincode, contact_person, contact_phone, contact_email, department, latitude, longitude, notes.</p>
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-blue-300 transition-colors">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-3">Drag & drop or click to select</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleImport}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                        disabled={importLoading}
                        data-testid="import-file-input"
                      />
                    </div>
                    {importLoading && (
                      <div className="flex items-center gap-2 text-sm text-blue-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing file...
                      </div>
                    )}
                    <Button variant="link" size="sm" onClick={downloadTemplate} className="text-blue-600 p-0 h-auto" data-testid="download-template-btn">
                      <Download className="w-3 h-3 mr-1" />
                      Download CSV template
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {/* Add Customer */}
            {isAdmin && (
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-customer-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Customer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit' }}>Add New Customer</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Organization Name *</Label>
                      <Input
                        value={newCustomer.name}
                        onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                        placeholder="e.g., Municipal Corporation of Greater Mumbai"
                        data-testid="customer-name-input"
                      />
                    </div>
                    <div>
                      <Label>Code</Label>
                      <Input
                        value={newCustomer.code}
                        onChange={(e) => setNewCustomer({ ...newCustomer, code: e.target.value.toUpperCase() })}
                        placeholder="e.g., MCGM"
                      />
                    </div>
                    <div>
                      <Label>Category *</Label>
                      <Select value={newCustomer.category} onValueChange={(v) => setNewCustomer({ ...newCustomer, category: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Website + Office Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Website</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newCustomer.website}
                          onChange={(e) => setNewCustomer({ ...newCustomer, website: e.target.value })}
                          placeholder="e.g., mcgm.gov.in"
                          className="flex-1"
                          data-testid="customer-website-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleFetchFromWebsite('create')}
                          disabled={fetchingWebsite || !newCustomer.website}
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 whitespace-nowrap"
                          data-testid="fetch-address-btn"
                        >
                          {fetchingWebsite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4 mr-1" />}
                          {fetchingWebsite ? 'Parsing...' : 'Fetch Address'}
                        </Button>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">AI will extract address from the website</p>
                    </div>
                    <div>
                      <Label>Office Type *</Label>
                      <Select value={newCustomer.office_type} onValueChange={(v) => setNewCustomer({ ...newCustomer, office_type: v, office_type_other: v !== 'others' ? '' : newCustomer.office_type_other })}>
                        <SelectTrigger data-testid="office-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OFFICE_TYPES.map(ot => (
                            <SelectItem key={ot.value} value={ot.value}>{ot.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {newCustomer.office_type === 'others' && (
                        <Input
                          value={newCustomer.office_type_other}
                          onChange={(e) => setNewCustomer({ ...newCustomer, office_type_other: e.target.value })}
                          placeholder="Specify office type *"
                          className="mt-2 border-amber-300"
                          data-testid="office-type-other-input"
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={newCustomer.address}
                      onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                      placeholder="Full address"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>City *</Label>
                      <Input
                        value={newCustomer.city}
                        onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                        placeholder="Mumbai"
                        data-testid="customer-city-input"
                      />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <Select value={newCustomer.state} onValueChange={(v) => setNewCustomer({ ...newCustomer, state: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Pincode</Label>
                      <Input
                        value={newCustomer.pincode}
                        onChange={(e) => setNewCustomer({ ...newCustomer, pincode: e.target.value })}
                        placeholder="400001"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">Location (Lat/Long)</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleGeocode('create')}
                        disabled={geocoding}
                        data-testid="geocode-btn"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        {geocoding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Crosshair className="w-4 h-4 mr-1" />}
                        {geocoding ? 'Searching...' : 'Fetch from Address'}
                      </Button>
                    </div>

                    {geocodeSuggestions.length > 0 && (
                      <div className="border border-blue-200 rounded-lg p-2 bg-blue-50 space-y-1 max-h-32 overflow-y-auto">
                        <p className="text-xs text-blue-600 font-medium px-1">Select a location:</p>
                        {geocodeSuggestions.map((s, i) => (
                          <div
                            key={`${s.lat}-${s.lng}`}
                            onClick={() => selectGeocodeSuggestion(s, 'create')}
                            className="text-xs p-2 bg-white rounded cursor-pointer hover:bg-blue-100 transition-colors truncate"
                            data-testid={`geocode-suggestion-${i}`}
                          >
                            <Navigation className="w-3 h-3 inline mr-1 text-blue-500" />
                            {s.display_name} ({s.lat.toFixed(4)}, {s.lng.toFixed(4)})
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Latitude</Label>
                        <Input
                          type="number"
                          step="any"
                          value={newCustomer.location.lat}
                          onChange={(e) => setNewCustomer({ ...newCustomer, location: { ...newCustomer.location, lat: parseFloat(e.target.value) || 0 } })}
                          placeholder="18.9322"
                          data-testid="customer-lat-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Longitude</Label>
                        <Input
                          type="number"
                          step="any"
                          value={newCustomer.location.lng}
                          onChange={(e) => setNewCustomer({ ...newCustomer, location: { ...newCustomer.location, lng: parseFloat(e.target.value) || 0 } })}
                          placeholder="72.8347"
                          data-testid="customer-lng-input"
                        />
                      </div>
                    </div>

                    {/* Map Picker */}
                    <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: '200px' }}>
                      <MapContainer
                        center={[newCustomer.location.lat || 19.076, newCustomer.location.lng || 72.877]}
                        zoom={newCustomer.location.lat ? 14 : 5}
                        style={{ height: '100%', width: '100%' }}
                        key={`create-${newCustomer.location.lat}-${newCustomer.location.lng}`}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; OpenStreetMap'
                        />
                        <MapClickHandler onMapClick={(lat, lng) => handleMapClick(lat, lng, 'create')} />
                        {newCustomer.location.lat !== 0 && newCustomer.location.lng !== 0 && (
                          <Marker position={[newCustomer.location.lat, newCustomer.location.lng]} />
                        )}
                      </MapContainer>
                      <p className="text-xs text-gray-400 text-center py-1 bg-gray-50">Click map to pin location</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Contact Person</Label>
                      <Input
                        value={newCustomer.contact_person}
                        onChange={(e) => setNewCustomer({ ...newCustomer, contact_person: e.target.value })}
                        placeholder="Name"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={newCustomer.contact_phone}
                        onChange={(e) => setNewCustomer({ ...newCustomer, contact_phone: e.target.value })}
                        placeholder="+91 22 12345678"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newCustomer.contact_email}
                        onChange={(e) => setNewCustomer({ ...newCustomer, contact_email: e.target.value })}
                        placeholder="contact@org.gov.in"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Department</Label>
                    <Input
                      value={newCustomer.department}
                      onChange={(e) => setNewCustomer({ ...newCustomer, department: e.target.value })}
                      placeholder="e.g., Water Supply, Solid Waste Management"
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={newCustomer.notes}
                      onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                      placeholder="Additional notes..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="flex-1">Cancel</Button>
                    <Button onClick={handleCreateCustomer} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="customer-submit-btn">
                      Create Customer
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          </div>
        </div>

        {/* Filters */}
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="customer-search-input"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterState} onValueChange={setFilterState}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Customers List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : customers.length === 0 ? (
          <Card className="border-gray-200">
            <CardContent className="text-center py-12">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No customers found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customers.map((customer) => (
              <Card key={customer.id} className="border-gray-200 hover:border-blue-200 transition-all" data-testid={`customer-card-${customer.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={getCategoryColor(customer.category)}>
                          {customer.code || customer.category.replace('_', ' ')}
                        </Badge>
                        {customer.status === 'inactive' && (
                          <Badge variant="outline" className="bg-red-50 text-red-700">Inactive</Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 mt-2 truncate">{customer.name}</h3>
                      <p className="text-sm text-gray-500">{customer.city}, {customer.state}</p>
                    </div>
                    {isAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingCustomer(customer)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openGoogleMaps(customer)}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View on Map
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteCustomer(customer.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  <div className="mt-4 space-y-2 text-sm">
                    {customer.website && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Globe className="w-4 h-4 text-emerald-400" />
                        <a href={customer.website.startsWith('http') ? customer.website : `https://${customer.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{customer.website}</a>
                      </div>
                    )}
                    {customer.office_type && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Landmark className="w-4 h-4 text-amber-400" />
                        <span>{customer.office_type === 'others' ? customer.office_type_other : OFFICE_TYPES.find(o => o.value === customer.office_type)?.label || customer.office_type}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex items-start gap-2 text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="truncate">{customer.address}</span>
                      </div>
                    )}
                    {customer.location?.lat && customer.location?.lng && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Crosshair className="w-4 h-4 text-blue-400" />
                        <span className="text-xs font-mono">{customer.location.lat.toFixed(4)}, {customer.location.lng.toFixed(4)}</span>
                      </div>
                    )}
                    {customer.contact_person && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <User className="w-4 h-4 text-gray-400" />
                        {customer.contact_person}
                      </div>
                    )}
                    {customer.contact_phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4 text-gray-400" />
                        {customer.contact_phone}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Activity className="w-4 h-4" />
                      {customer.activity_count || 0} activities
                    </div>
                    {customer.pending_activities > 0 && (
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                        {customer.pending_activities} pending
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Customer Dialog */}
        <Dialog open={!!editingCustomer} onOpenChange={() => setEditingCustomer(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Edit Customer</DialogTitle>
            </DialogHeader>
            {editingCustomer && (
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Organization Name</Label>
                  <Input
                    value={editingCustomer.name}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Code</Label>
                    <Input
                      value={editingCustomer.code || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, code: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={editingCustomer.category} onValueChange={(v) => setEditingCustomer({ ...editingCustomer, category: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Website + Office Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Website</Label>
                    <div className="flex gap-2">
                      <Input
                        value={editingCustomer.website || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, website: e.target.value })}
                        placeholder="e.g., mcgm.gov.in"
                        className="flex-1"
                        data-testid="edit-customer-website-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleFetchFromWebsite('edit')}
                        disabled={fetchingWebsiteEdit || !editingCustomer.website}
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 whitespace-nowrap"
                        data-testid="edit-fetch-address-btn"
                      >
                        {fetchingWebsiteEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4 mr-1" />}
                        {fetchingWebsiteEdit ? 'Parsing...' : 'Fetch Address'}
                      </Button>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">AI will extract address from the website</p>
                  </div>
                  <div>
                    <Label>Office Type</Label>
                    <Select value={editingCustomer.office_type || 'head_office'} onValueChange={(v) => setEditingCustomer({ ...editingCustomer, office_type: v, office_type_other: v !== 'others' ? '' : (editingCustomer.office_type_other || '') })}>
                      <SelectTrigger data-testid="edit-office-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OFFICE_TYPES.map(ot => (
                          <SelectItem key={ot.value} value={ot.value}>{ot.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {editingCustomer.office_type === 'others' && (
                      <Input
                        value={editingCustomer.office_type_other || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, office_type_other: e.target.value })}
                        placeholder="Specify office type *"
                        className="mt-2 border-amber-300"
                        data-testid="edit-office-type-other-input"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={editingCustomer.address || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={editingCustomer.city}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Select value={editingCustomer.state} onValueChange={(v) => setEditingCustomer({ ...editingCustomer, state: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Pincode</Label>
                    <Input
                      value={editingCustomer.pincode || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, pincode: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Location (Lat/Long)</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleGeocode('edit')}
                      disabled={geocodingEdit}
                      data-testid="geocode-edit-btn"
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    >
                      {geocodingEdit ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Crosshair className="w-4 h-4 mr-1" />}
                      {geocodingEdit ? 'Searching...' : 'Fetch from Address'}
                    </Button>
                  </div>

                  {geocodeSuggestionsEdit.length > 0 && (
                    <div className="border border-blue-200 rounded-lg p-2 bg-blue-50 space-y-1 max-h-32 overflow-y-auto">
                      <p className="text-xs text-blue-600 font-medium px-1">Select a location:</p>
                      {geocodeSuggestionsEdit.map((s, i) => (
                        <div
                          key={`${s.lat}-${s.lng}`}
                          onClick={() => selectGeocodeSuggestion(s, 'edit')}
                          className="text-xs p-2 bg-white rounded cursor-pointer hover:bg-blue-100 transition-colors truncate"
                        >
                          <Navigation className="w-3 h-3 inline mr-1 text-blue-500" />
                          {s.display_name} ({s.lat.toFixed(4)}, {s.lng.toFixed(4)})
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Latitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editingCustomer.location?.lat || 0}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, location: { ...editingCustomer.location, lat: parseFloat(e.target.value) || 0 } })}
                        data-testid="edit-customer-lat-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Longitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editingCustomer.location?.lng || 0}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, location: { ...editingCustomer.location, lng: parseFloat(e.target.value) || 0 } })}
                        data-testid="edit-customer-lng-input"
                      />
                    </div>
                  </div>

                  {/* Map Picker for Edit */}
                  <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: '200px' }}>
                    <MapContainer
                      center={[editingCustomer.location?.lat || 19.076, editingCustomer.location?.lng || 72.877]}
                      zoom={editingCustomer.location?.lat ? 14 : 5}
                      style={{ height: '100%', width: '100%' }}
                      key={`edit-${editingCustomer.location?.lat}-${editingCustomer.location?.lng}`}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap'
                      />
                      <MapClickHandler onMapClick={(lat, lng) => handleMapClick(lat, lng, 'edit')} />
                      {editingCustomer.location?.lat && editingCustomer.location?.lng && (
                        <Marker position={[editingCustomer.location.lat, editingCustomer.location.lng]} />
                      )}
                    </MapContainer>
                    <p className="text-xs text-gray-400 text-center py-1 bg-gray-50">Click map to pin location</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Contact Person</Label>
                    <Input
                      value={editingCustomer.contact_person || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, contact_person: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={editingCustomer.contact_phone || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, contact_phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={editingCustomer.contact_email || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, contact_email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setEditingCustomer(null)} className="flex-1">Cancel</Button>
                  <Button onClick={handleUpdateCustomer} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
